import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Building2, Wallet, Users, ChevronDown, ChevronRight, ChevronLeft,
  AlertTriangle, Maximize2, Minimize2, CalendarDays,
  Stethoscope, Pencil, BarChart3, Radar, Lock,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson } from '../lib/api.js'
import {
  num, stripStars, bXunit, bAdvancePayer, bTotal, bAdvance, bMissingClaimRef,
  bMismatch, sumTx, doctorName, paymentOf, isNoPay,
  TEAL, buildPmIndex, sortPayMethods } from '../lib/billing.js'
import {
  BillColgroup, BillTotalCells, BillingTable, PaymentBadge,
} from '../components/billing/BillingTable.jsx'
import { usePersistedState } from '../lib/persist.js'
import { useMe } from '../lib/me.js'
import { useXanoRealtime } from '../lib/realtime.js'
import { nameInitial } from '../lib/text.js'
import RadarChart from '../components/RadarChart.jsx'
import BarCompareChart from '../components/BarCompareChart.jsx'
import {
  toISO, parseISO, addDays, asList,
  PALETTE, BLUE, PURPLE, Card,
} from './statistic/statShared.jsx'
import {
  Page, PageHeader, Button, IconButton, Field, Input, Select, SearchInput,
  FilterCard, useFilters, Segmented, Loading, EmptyState, NoMatchState, ErrorState,
  InlineError, Banner, Badge, Drawer, DrawerFooter, PatientLink, PatientName, useToast,
  fmtMoney, fmtDate, fmtDateText, fmtMonth, dateParts, bkkTodayISO, TH_DAYS_SHORT,
} from '../kit'

/* -------------------------------------------------------------------------- */
/* constants                                                                  */
/* -------------------------------------------------------------------------- */

// Both revenue series sit in the blue family (page theme). They are alternative
// metrics on ONE chart, not two series side by side, so they only have to be
// told apart in the ยอดจริง / คลินิกจริง toggle — hence the lighter cyan for the
// clinic cut rather than a clashing hue.
const TOTAL_ACTUAL = BLUE      // priceActualTotal series
const CLINIC_ACTUAL = '#0891b2' // priceActualClinic series (cyan-600)
const INDIGO = '#6366f1'     // เบิก (priceadvance) accent

// Whole baht, rounded with Math.round BEFORE the kit formats it — the exact
// rounding the old statShared `fmt` / lib/billing.js `fmtBill` used, so every
// figure on this page matches BillingTable and the patient ledger to the baht
// (fmtMoney alone rounds a negative half away from zero). Display only.
const fmt = (n) => fmtMoney(n == null || n === '' ? n : Math.round(Number(n)))

// Segmented option "icons": the coloured dot that ties the toggle to its series.
const TotalDot = () => <span className="w-2 h-2 rounded-full" style={{ background: TOTAL_ACTUAL }} />
const ClinicDot = () => <span className="w-2 h-2 rounded-full" style={{ background: CLINIC_ACTUAL }} />

// 'dd/mm/yyyy อา.' — the visit date with its weekday, for the ledger rows.
function dayWithDow(v) {
  const p = dateParts(v)
  return p ? `${fmtDate(v)} ${TH_DAYS_SHORT[p.dow]}` : ''
}

/* -------------------------------------------------------------------------- */
/* billing / treatment field access — MOVED to src/lib/billing.js 2026-08-12   */
/* so the per-patient การรักษา ledger computes money identically. Imported     */
/* above; do not re-declare them here.                                        */
/* -------------------------------------------------------------------------- */


/* -------------------------------------------------------------------------- */
/* data shaping                                                               */
/* -------------------------------------------------------------------------- */

function summarizeBranches(txs, branchName, pmById) {
  const m = new Map()
  for (const tx of txs) {
    if (tx?.active === false) continue
    const bid = String(tx.branch_id ?? tx.branch?.id ?? '0')
    if (!m.has(bid)) m.set(bid, { bid, name: branchName[bid] || `สาขา #${bid}`, tx: 0, patients: new Set(), total: 0, clinic: 0, doctor: 0, patient: 0, advance: 0, noPayTotal: 0, noPayCount: 0, noClaimRef: 0 })
    const g = m.get(bid)
    g.tx += 1
    g.noClaimRef += (tx.billing || []).filter(bMissingClaimRef).length
    g.patients.add(tx.patient_id ?? tx.patient?.id)
    const s = sumTx(tx)
    g.total += s.total; g.clinic += s.clinic; g.doctor += s.doctor; g.patient += s.patient; g.advance += s.advance
    const pay = paymentOf(tx, pmById)
    // alert keys off เก็บเอง (priceXunit): money collected from the patient but with no method set
    if (isNoPay(pay.key) && Math.abs(s.patient) > 0.5) { g.noPayTotal += s.patient; g.noPayCount += 1 }
  }
  return [...m.values()].map((g) => ({ ...g, patientCount: g.patients.size })).sort((a, b) => b.total - a.total)
}

function summarizePayments(txs, pmById) {
  const m = new Map()
  let grandCollect = 0
  for (const tx of txs) {
    if (tx?.active === false) continue
    const { key, name, color } = paymentOf(tx, pmById)
    if (!m.has(key)) m.set(key, { key, name, color: color || null, count: 0, pts: new Set(), total: 0, clinic: 0, doctor: 0, patient: 0, advance: 0 })
    const g = m.get(key)
    if (!g.color && color) g.color = color
    g.count += 1
    g.pts.add(tx.patient_id ?? tx.patient?.id)
    const s = sumTx(tx)
    g.total += s.total; g.clinic += s.clinic; g.doctor += s.doctor; g.patient += s.patient; g.advance += s.advance
    grandCollect += s.patient // เก็บเอง drives end-of-day reconciliation → base % on it
  }
  const rows = [...m.values()].sort((a, b) => b.patient - a.patient) // sort by เก็บเอง (money to reconcile)
    .map((r, i) => ({ ...r, patientCount: r.pts.size, color: isNoPay(r.key) ? '#dc2626' : (r.color || PALETTE[i % PALETTE.length]), pct: grandCollect > 0 ? (r.patient / grandCollect) * 100 : 0 }))
  return { rows, grand: grandCollect }
}

// income grouped by doctor within a branch; doctors with 0 actual-total income
// are split out (listed by name in a note, not shown as rows).
function summarizeDoctors(txs) {
  const m = new Map()
  for (const tx of txs) {
    if (tx?.active === false) continue
    const id = String(tx.doctor_id ?? tx.doctor?.id ?? doctorName(tx) ?? 'unknown')
    const name = doctorName(tx) || '—'
    const img = tx.doctor?.profile_img || tx.doctor?.image || tx.doctor?.img || ''
    if (!m.has(id)) m.set(id, { id, name, img, cases: 0, patients: new Set(), total: 0, patient: 0, advance: 0, clinic: 0, doctor: 0 })
    const g = m.get(id)
    if (!g.img && img) g.img = img
    g.cases += 1
    g.patients.add(tx.patient_id ?? tx.patient?.id)
    const s = sumTx(tx)
    g.total += s.total; g.patient += s.patient; g.advance += s.advance; g.clinic += s.clinic; g.doctor += s.doctor
  }
  const all = [...m.values()].map((g) => ({ ...g, patientCount: g.patients.size }))
  const earners = all.filter((g) => Math.abs(g.total) > 0.5).sort((a, b) => b.total - a.total)
  const zero = all.filter((g) => Math.abs(g.total) <= 0.5).map((g) => g.name).filter(Boolean)
  return { earners, zero }
}


function groupByPatient(txs) {
  const m = new Map()
  for (const tx of txs) {
    if (tx?.active === false) continue
    const pid = tx.patient_id ?? tx.patient?.id ?? `nk-${tx.id}`
    if (!m.has(pid)) m.set(pid, { pid, name: stripStars(tx.fullname) || stripStars(tx.patient?.name) || '—', HN: tx.HN ?? tx.patient?.HN ?? '', pt_status: tx.pt_status || '', txs: [], total: 0, clinic: 0, doctor: 0, patient: 0, advance: 0 })
    const g = m.get(pid)
    g.txs.push(tx)
    const s = sumTx(tx)
    g.total += s.total; g.clinic += s.clinic; g.doctor += s.doctor; g.patient += s.patient; g.advance += s.advance
  }
  const list = [...m.values()]
  for (const g of list) g.txs.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
  return list.sort((a, b) => b.total - a.total)
}

// day feed: paginate the ledger envelope.
// IMPORTANT: bound the loop by `pageTotal` (the real page count — normally 1 for a
// single branch-day), NOT by `nextPage`. The old feed's `nextPage` could stay
// non-null even when it kept re-returning the same set, which used to make this
// fire the heavy request up to 30× sequentially ("sometimes loads forever"). We
// fetch page 1, read pageTotal, fetch any remaining pages in PARALLEL, dedupe by id.
//
// 🚨 MOVED OFF `/api/treatments?type=dashboard` (Xano api 153) 2026-08-26 →
// `/api/treatment-day` (Xano api 645, claude group), a reduced fork that drops the
// six addons this page never reads (chart+consent, lab, ticket, chart_count,
// next_appointment, creator) plus a per-billing-line COUNT query.
// api 153 IS DELIBERATELY LEFT UNTOUCHED: Bubble is still its majority caller —
// 205 of 300 requests over a measured 2h39m window, 138 of them type=dashboard,
// 2.3× this app's own dashboard traffic. See functions/api/treatment-day.js.
// Do NOT "consolidate" this back onto /api/treatments.
async function fetchBranchDay(branchId, day) {
  // per_page 200, not the proxy's default 50: the busiest branch-day measured is
  // ~80 cases, so one page covers every real day and the pageTotal loop below
  // stays a no-op. The old range path hard-coded 500/page but ignored `page`
  // entirely, so this is the first time paging here actually works.
  const base = { branch_id: branchId, date: day, per_page: 200 }
  const first = await apiGet('/api/treatment-day', { ...base, page: 1 })
  const rows = asList(first)
  const env = first && !Array.isArray(first) ? first : {}
  const pageTotal = Math.min(10, Math.max(1, Number(env.pageTotal) || 1))
  if (pageTotal > 1 && rows.length > 0) {
    const rest = await Promise.all(
      Array.from({ length: pageTotal - 1 }, (_, i) =>
        apiGet('/api/treatment-day', { ...base, page: i + 2 }).catch(() => null)),
    )
    for (const r of rest) rows.push(...asList(r))
  }
  return rows
}

async function fetchDay(day) {
  // 500-ROW CAP: the all-branches feed (branch_id=0) caps at 500 rows/page with
  // broken pagination — a single busy day is >500 rows across all branches, so
  // records get silently dropped (whole doctors go missing). Fetch EACH branch
  // separately (each branch/day is small, well under 500) and combine.
  //
  // NO ±1 DAY WINDOW ANY MORE (2026-08-26). The old feed was given an `end_date`
  // purely to reach the code path that accepted a date RANGE — and that path
  // hard-coded per_page 500 and ignored `page`. The price was fetching THREE
  // calendar days per branch to display one: ~3× the rows and ~3× the time, on
  // top of the addon fan-out. /api/treatment-day has no end_date input at all;
  // it does an exact match on the indexed treatment.date, so the window is
  // impossible by construction.
  // The `=== day` filter below is kept as a cheap invariant, NOT as a correction —
  // if it ever drops a row, the server-side date match is wrong and that is a bug
  // to fix in Xano api 645, not to paper over here.

  let branchIds = []
  try {
    const br = await cachedGet('/api/branches')
    branchIds = asList(br).map((b) => b.id ?? b.branch_id).filter((v) => v != null && v !== '')
  } catch { /* fall back below */ }
  if (!branchIds.length) branchIds = [0] // last-resort: single all-branch call

  // `partial` = at least one branch fetch failed, so `out` is missing rows. The
  // caller uses this to avoid caching an incomplete day (see fetchDayCached).
  let partial = false
  const perBranch = await Promise.all(
    branchIds.map((bid) => fetchBranchDay(bid, day)
      .catch(() => { partial = true; return [] })),
  )

  // Dedupe by treatment id, then keep only the selected Bangkok calendar day.
  const seen = new Set()
  const out = []
  for (const rows of perBranch) {
    for (const t of rows) {
      const id = t?.id ?? t?.treatment_id
      if (id != null) { if (seen.has(id)) continue; seen.add(id) }
      if (String(t?.date ?? '').slice(0, 10) === day) out.push(t)
    }
  }
  return { rows: out, partial }
}

// Past days are payment-locked (tx.date !== bkkToday ⇒ no edits) so their feed is
// stable — cache it 1h. Today stays live and is never cached. `force` (an edit
// bumped reloadKey) invalidates and refetches. We cache only complete results, so
// a transient per-branch failure never freezes a thin day for an hour.
const _dayCache = new Map() // day -> { rows, exp }
const DAY_TTL = 60 * 60 * 1000

async function fetchDayCached(day, force = false) {
  if (day === bkkTodayISO()) return (await fetchDay(day)).rows // live, never cached
  if (force) _dayCache.delete(day)
  const hit = _dayCache.get(day)
  if (hit && hit.exp > Date.now()) return hit.rows
  const { rows, partial } = await fetchDay(day)
  if (!partial) _dayCache.set(day, { rows, exp: Date.now() + DAY_TTL })
  return rows
}

// month star chart: revenue/monthly returns per-branch priceTotal(=ΣpriceActualTotal),
// priceClinic(=ΣpriceActualClinic) — fast + cached, no heavy dashboard scan.
function monthChartFromRevenue(rows, branchName) {
  const m = new Map()
  for (const r of asList(rows)) {
    const bid = String(r.branch)
    if (!m.has(bid)) m.set(bid, { bid, total: 0, clinic: 0 })
    const g = m.get(bid)
    g.total += num(r.priceTotal)
    g.clinic += num(r.priceClinic)
  }
  return [...m.values()].filter((g) => g.total || g.clinic).sort((a, b) => b.total - a.total)
    .map((g) => ({ label: branchName[g.bid] || `สาขา #${g.bid}`, total: g.total, clinic: g.clinic }))
}

/* -------------------------------------------------------------------------- */
/* page                                                                       */
/* -------------------------------------------------------------------------- */

export default function Treatments() {
  // Bangkok "today", not the browser clock — matches every other page and the
  // live-vs-cached rule in loadDay (a device on UTC would be a day behind before 07:00).
  const today = useMemo(() => bkkTodayISO(), [])
  // The day is a kit filter: remembered while you work, back to today on the
  // next Bangkok day (it used to stay on yesterday the morning after).
  const { filters: dayFilter, set: setDayFilter } = useFilters('treatments', { day: today }, { daily: ['day'] })
  const day = dayFilter.day || today
  const setDay = (v) => setDayFilter('day', v)

  const [branchName, setBranchName] = useState({})
  const [pmById, setPmById] = useState(() => new Map())
  const [rows, setRows] = useState(null)
  const [state, setState] = useState({ loading: true, err: '' })
  const [monthRows, setMonthRows] = useState(null)
  const [prevMonthRows, setPrevMonthRows] = useState(null)
  const [monthState, setMonthState] = useState({ loading: true, err: '' })

  const [selBranch, setSelBranch] = usePersistedState('tx:selBranch', null)
  const [openPatients, setOpenPatients] = useState(() => new Set())
  const [chartMetric, setChartMetric] = usePersistedState('tx:chartMetric', 'total') // total | clinic (both charts)
  const [billingSort, setBillingSort] = usePersistedState('tx:billingSort2', 'type') // type | patient | total (default รายการ)
  // ledger filters — NOT remembered: the search box holds patient names/HNs
  // (never browser storage), and doctor / payment options only exist for the
  // branch-day on screen.
  const [fPatient, setFPatient] = useState('')
  const [fPay, setFPay] = useState('all')
  const [fDoc, setFDoc] = useState('all')
  const [fMin, setFMin] = useState('')
  const [fMax, setFMax] = useState('')
  // treatment edit drawer
  const [editTx, setEditTx] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  // Only for skipping our own realtime echo — this page has no role gates.
  const me = useMe()
  const toast = useToast()

  const shiftDay = (delta) => setDay(toISO(addDays(parseISO(day), delta)))
  const d0 = parseISO(day)
  const monthLabel = fmtMonth(day, { long: true })
  const prevMonthLabel = fmtMonth(toISO(new Date(d0.getFullYear(), d0.getMonth() - 1, 1)), { long: true })
  const dayText = fmtDateText(day)

  // reference lists
  useEffect(() => {
    cachedGet('/api/branches')
      .then((br) => setBranchName(Object.fromEntries(asList(br).map((b) => [String(b.id ?? b.branch_id), b.name || b.branch_name || `สาขา #${b.id}`]))))
      .catch(() => {})
    cachedGet('/api/payment-methods')
      .then((pm) => setPmById(buildPmIndex(pm)))
      .catch(() => {})
  }, [])

  // selected day's treatments (all branches). Past days are cached 1h; today stays
  // live. A reloadKey bump (an edit) forces a fresh fetch, bypassing the cache.
  //
  // A NEW DAY is a first load: the old day's rows go and a spinner shows. A
  // reloadKey bump — an edit saved here, a realtime change from another desk, or
  // ลองอีกครั้ง — is a REFRESH: the rows stay on screen, dimmed, until the new
  // ones land, so the ledger never blinks away under someone reading it.
  const lastReloadKey = useRef(reloadKey)
  const lastDay = useRef(null)
  useEffect(() => {
    let alive = true
    const force = lastReloadKey.current !== reloadKey
    lastReloadKey.current = reloadKey
    const newDay = lastDay.current !== day
    lastDay.current = day
    setState({ loading: true, err: '' })
    if (newDay) setRows(null)
    fetchDayCached(day, force)
      .then((data) => { if (alive) { setRows(data); setState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setRows([]); setState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [day, reloadKey])
  // spinner only when there is nothing to show yet; otherwise dim what is there
  const firstLoad = state.loading && !rows?.length
  const refreshing = state.loading && !firstLoad
  const dim = `transition-opacity ${refreshing ? 'opacity-60' : ''}`

  // collapse expansions only when the DAY changes (not on edit-triggered reloads)
  useEffect(() => { setOpenPatients(new Set()) }, [day])

  // Live (2026-08-18): `treatment_update` now carries every treatment AND billing
  // row change (from the `patient_update - treatment` / `- billing` table triggers),
  // not just the two create endpoints it used to. A line billed at the counter or on
  // a patient page moves this ledger, whoever wrote it and from which app.
  const liveTimer = useRef(null)
  useEffect(() => () => clearTimeout(liveTimer.current), [])
  useXanoRealtime('treatment_update', (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    const emp = p && typeof p === 'object' ? p.employee_id : null
    if (emp != null && emp !== '' && me?.employee_id != null && String(emp) === String(me.employee_id)) return
    clearTimeout(liveTimer.current)
    liveTimer.current = setTimeout(() => { setReloadKey((k) => k + 1) }, 600)
  })

  // month star chart — per-branch actual sums for BOTH the selected month (current
  // series) and the previous month (grey compare series), fetched in parallel.
  useEffect(() => {
    let alive = true
    const curStart = toISO(new Date(d0.getFullYear(), d0.getMonth(), 1))
    const curEnd = toISO(new Date(d0.getFullYear(), d0.getMonth() + 1, 0))
    const prevStart = toISO(new Date(d0.getFullYear(), d0.getMonth() - 1, 1))
    const prevEnd = toISO(new Date(d0.getFullYear(), d0.getMonth(), 0)) // last day of prev month
    setMonthState({ loading: true, err: '' })
    Promise.all([
      apiGet('/api/revenue/monthly', { start_date: curStart, end_date: curEnd, branch_id: 0, exclude_nhso: false }),
      apiGet('/api/revenue/monthly', { start_date: prevStart, end_date: prevEnd, branch_id: 0, exclude_nhso: false }),
    ])
      .then(([cur, prev]) => {
        if (!alive) return
        setMonthRows(Array.isArray(cur) ? cur : asList(cur))
        setPrevMonthRows(Array.isArray(prev) ? prev : asList(prev))
        setMonthState({ loading: false, err: '' })
      })
      .catch((e) => { if (alive) { setMonthRows([]); setPrevMonthRows([]); setMonthState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [day.slice(0, 7)]) // refetch only when the month changes

  const branches = useMemo(() => (rows ? summarizeBranches(rows, branchName, pmById) : []), [rows, branchName, pmById])
  const grand = useMemo(() => branches.reduce((a, b) => ({
    total: a.total + b.total, clinic: a.clinic + b.clinic, doctor: a.doctor + b.doctor,
    patient: a.patient + b.patient, advance: a.advance + b.advance,
    tx: a.tx + b.tx, patients: a.patients + b.patientCount, noPayTotal: a.noPayTotal + b.noPayTotal, noPayCount: a.noPayCount + b.noPayCount,
  }), { total: 0, clinic: 0, doctor: 0, patient: 0, advance: 0, tx: 0, patients: 0, noPayTotal: 0, noPayCount: 0 }), [branches])

  // เบิก (all non-patient money = รวม − เก็บเอง) broken down by advance-pay payer
  // (ปกส./AIA/Prudential/…). Per line the insurer portion = priceActualTotal − priceXunit.
  const advanceByPayer = useMemo(() => {
    const m = new Map()
    for (const t of rows || []) {
      if (t?.active === false) continue
      for (const b of t.billing || []) {
        const portion = bTotal(b) - bXunit(b)
        if (Math.abs(portion) < 0.5) continue
        const payer = bAdvancePayer(b) || 'อื่นๆ'
        m.set(payer, (m.get(payer) || 0) + portion)
      }
    }
    return [...m.entries()].filter(([, v]) => Math.abs(v) >= 0.5).sort((a, b) => b[1] - a[1])
  }, [rows])

  // เบิกแต่ไม่มีรหัสเคลม (Ram, 2026-10-21) — every line billed to a payer other than
  // สปสช./ปกส. with an empty claimRef, across ALL branches of the day (lib/billing.js
  // #bMissingClaimRef). One entry per line, so the banner can jump to each case.
  const missingClaims = useMemo(() => {
    const out = []
    for (const t of rows || []) {
      if (t?.active === false) continue
      for (const b of t.billing || []) {
        if (!bMissingClaimRef(b)) continue
        out.push({
          key: b.id ?? `${t.id}-${out.length}`,
          bid: String(t.branch_id ?? t.branch?.id ?? '0'),
          pid: t.patient_id ?? t.patient?.id ?? `nk-${t.id}`,
          name: stripStars(t.fullname) || stripStars(t.patient?.name) || '—',
          payer: bAdvancePayer(b),
          amount: bAdvance(b),
        })
      }
    }
    return out
  }, [rows])

  const mk = chartMetric // 'total' | 'clinic'
  // day chart: branches carry total (ΣpriceActualTotal) + clinic (ΣpriceActualClinic)
  const dayChart = useMemo(() => branches.map((b) => ({ label: b.name, total: b.total, clinic: b.clinic })), [branches])
  const monthChart = useMemo(() => (monthRows ? monthChartFromRevenue(monthRows, branchName) : []), [monthRows, branchName])
  const prevMonthChart = useMemo(() => (prevMonthRows ? monthChartFromRevenue(prevMonthRows, branchName) : []), [prevMonthRows, branchName])
  const toAxes = (rows) => rows.filter((r) => r[mk]).sort((a, b) => b[mk] - a[mk]).map((r) => ({ key: r.label, label: r.label, value: r[mk] }))
  const dayAxes = useMemo(() => toAxes(dayChart), [dayChart, mk])
  // month radar overlays current month (value) + previous month (prev, grey), keyed by branch.
  const monthAxes = useMemo(() => {
    const curBy = new Map(monthChart.map((r) => [r.label, r[mk] || 0]))
    const prevBy = new Map(prevMonthChart.map((r) => [r.label, r[mk] || 0]))
    return [...new Set([...curBy.keys(), ...prevBy.keys()])]
      .map((label) => ({ key: label, label, value: curBy.get(label) || 0, prev: prevBy.get(label) || 0 }))
      .filter((a) => a.value || a.prev)
      .sort((a, b) => b.value - a.value)
  }, [monthChart, prevMonthChart, mk])
  const dayAxesSum = useMemo(() => dayChart.reduce((s, r) => s + r[mk], 0), [dayChart, mk])
  const monthAxesSum = useMemo(() => monthChart.reduce((s, r) => s + r[mk], 0), [monthChart, mk])
  const prevMonthAxesSum = useMemo(() => prevMonthChart.reduce((s, r) => s + r[mk], 0), [prevMonthChart, mk])

  const activeBid = useMemo(() => {
    if (!branches.length) return null
    if (selBranch && branches.some((b) => b.bid === selBranch)) return selBranch
    return branches[0].bid
  }, [branches, selBranch])
  const activeBranch = branches.find((b) => b.bid === activeBid) || null

  const branchTxs = useMemo(() => (rows && activeBid ? rows.filter((t) => String(t.branch_id ?? t.branch?.id ?? '0') === activeBid) : []), [rows, activeBid])
  const payments = useMemo(() => summarizePayments(branchTxs, pmById), [branchTxs, pmById])
  const doctors = useMemo(() => summarizeDoctors(branchTxs), [branchTxs])
  const patients = useMemo(() => groupByPatient(branchTxs), [branchTxs])

  // ledger filters — patient name/HN, payment method, doctor, billing (total) min–max
  const payOptions = useMemo(() => payments.rows.map((r) => ({ key: r.key, name: r.name })), [payments])
  const docOptions = useMemo(() => {
    const m = new Map()
    for (const tx of branchTxs) { const n = doctorName(tx); if (n) m.set(n, (m.get(n) || 0) + 1) }
    return [...m.keys()].sort((a, b) => a.localeCompare(b, 'th'))
  }, [branchTxs])
  // real payment-method names for the edit drawer — sourced from treatments' paymentMethod
  const payMethodNames = useMemo(() => {
    const set = new Set()
    for (const t of rows || []) { const nm = t.payment_display || t.paymentMethod; if (nm && String(nm).trim()) set.add(String(nm).trim()) }
    return [...set].sort((a, b) => a.localeCompare(b, 'th'))
  }, [rows])
  const filteredPatients = useMemo(() => {
    const q = fPatient.trim().toLowerCase()
    const min = fMin === '' ? -Infinity : Number(fMin)
    const max = fMax === '' ? Infinity : Number(fMax)
    return patients.filter((p) => {
      if (q && !(`${p.name} ${p.HN}`.toLowerCase().includes(q))) return false
      if (p.total < min || p.total > max) return false
      if (fPay !== 'all' && !p.txs.some((tx) => paymentOf(tx, pmById).key === fPay)) return false
      if (fDoc !== 'all' && !p.txs.some((tx) => doctorName(tx) === fDoc)) return false
      return true
    })
  }, [patients, fPatient, fPay, fDoc, fMin, fMax, pmById])
  const filterActive = fPatient !== '' || fPay !== 'all' || fDoc !== 'all' || fMin !== '' || fMax !== ''
  const clearFilters = () => { setFPatient(''); setFPay('all'); setFDoc('all'); setFMin(''); setFMax('') }

  const togglePatient = (pid) => setOpenPatients((s) => { const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n })
  const allOpen = filteredPatients.length > 0 && filteredPatients.every((p) => openPatients.has(p.pid))
  const expandAll = () => setOpenPatients(new Set(filteredPatients.map((p) => p.pid)))
  const collapseAll = () => setOpenPatients(new Set())
  // From the missing-claim banner: select that case's branch, clear the ledger filters
  // that could hide it, open the patient, and scroll it into view once it renders.
  const jumpToPatient = (bid, pid) => {
    clearFilters()
    setSelBranch(bid)
    setOpenPatients((s) => new Set(s).add(pid))
    setTimeout(() => document.getElementById(`tx-pt-${pid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80)
  }

  return (
    <Page>
      <PageHeader
        title="การรักษา"
        subtitle="รายได้แต่ละสาขา และตรวจสอบบิลรายเคส"
        meta={(
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span>{fmtDateText(day, { weekday: true })}</span>
            {refreshing && <Loading variant="inline" label="กำลังอัปเดต…" />}
          </span>
        )}
        actions={(
          /* single-day picker */
          <div className="flex items-center gap-1.5">
            <IconButton icon={ChevronLeft} label="วันก่อนหน้า" variant="secondary" onClick={() => shiftDay(-1)} />
            <Input type="date" value={day} max={today} aria-label="วันที่"
              onChange={(e) => e.target.value && setDay(e.target.value)} className="!w-auto !py-1.5" />
            <IconButton icon={ChevronRight} label="วันถัดไป" variant="secondary" onClick={() => shiftDay(1)}
              disabled={day >= today} disabledReason="ยังไม่ถึงวันถัดไป" />
            {day !== today && <Button variant="secondary" onClick={() => setDay(today)}>วันนี้</Button>}
          </div>
        )}
      />

      {/* ── branch income: mindmap + charts + per-branch table ──────────── */}
      <div className="space-y-6">
        <Card icon={<Building2 size={18} strokeWidth={1.75} />} title="รายได้แต่ละสาขา"
          subtitle={`${dayText} · คลิกสาขาเพื่อดูวิธีชำระเงินและรายการ`}
          right={<Segmented size="sm" value={chartMetric} onChange={setChartMetric}
            options={[{ value: 'total', label: 'ยอดจริง', icon: TotalDot }, { value: 'clinic', label: 'คลินิกจริง', icon: ClinicDot }]} />}>
          {/* mindmap on top (full-width strip); the two star charts on their own row below */}
          <div className="mb-5">
            <div className={dim}>
              <IncomeMindmap grand={grand} branchCount={branches.length} advanceByPayer={advanceByPayer} />
            </div>
            {/* day → month → prev-month context so the two charts' relationship is legible */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-slate-400 mb-2">
              <CalendarDays size={13} className="text-slate-400" strokeWidth={1.9} />
              <span>รายวัน = <b className="font-semibold text-slate-600">{dayText}</b></span>
              <ChevronRight size={11} className="text-slate-300" />
              <span>อยู่ในเดือน <b className="font-semibold text-slate-600">{monthLabel}</b></span>
              <ChevronRight size={11} className="text-slate-300" />
              <span>เทียบ <span className="font-medium text-slate-500">{prevMonthLabel}</span> <span className="text-slate-300">(เส้นประเทา)</span></span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ChartPane title={`รายวัน · ${dayText}`} loading={firstLoad} refreshing={refreshing} err={state.err}
                axes={dayAxes} sum={dayAxesSum} color={chartMetric === 'total' ? TOTAL_ACTUAL : CLINIC_ACTUAL} />
              <ChartPane title={`รายเดือน · ${monthLabel}`} loading={monthState.loading} err={monthState.err}
                axes={monthAxes} sum={monthAxesSum} color={chartMetric === 'total' ? TOTAL_ACTUAL : CLINIC_ACTUAL}
                compare prevLabel={prevMonthLabel} prevSum={prevMonthAxesSum} />
            </div>
          </div>

          {grand.noPayTotal !== 0 && (
            <Banner tone="error" className="mb-3">
              เก็บเงินจากคนไข้แต่ไม่ระบุวิธีชำระ {fmt(grand.noPayTotal)} ({grand.noPayCount} เคส)
            </Banner>
          )}
          {missingClaims.length > 0 && (
            <Banner tone="error" className="mb-3" title={`รายการเบิกที่ยังไม่มีรหัสเคลม ${missingClaims.length} รายการ`}>
              <div className="text-[12.5px] mb-1.5">ทุกการเบิก (ยกเว้น สปสช. / ปกส.) ต้องกรอกรหัสเคลมประกัน — คลิกชื่อเพื่อเปิดรายการ</div>
              <div className="flex flex-wrap gap-1.5">
                {missingClaims.length > 12 && <span className="self-center text-[12px] order-last">+{missingClaims.length - 12} รายการ (ดูป้ายในแต่ละสาขา)</span>}
                {missingClaims.slice(0, 12).map((m) => (
                  <button key={m.key} type="button" onClick={() => jumpToPatient(m.bid, m.pid)}
                    className="inline-flex items-center gap-1 rounded-full bg-white/70 ring-1 ring-inset ring-rose-200 px-2 py-0.5 text-[12px] hover:bg-white">
                    <span className="font-medium">{m.name}</span>
                    <span className="text-rose-500">· {m.payer} {fmt(m.amount)}</span>
                    {branches.length > 1 && <span className="text-rose-400">· {branchName[m.bid] || `สาขา #${m.bid}`}</span>}
                  </button>
                ))}
              </div>
            </Banner>
          )}

          {firstLoad ? (
            <Loading />
          ) : state.err ? (
            <ErrorState message={state.err} onRetry={() => setReloadKey((k) => k + 1)} />
          ) : !rows?.length ? (
            <EmptyState variant="plain" title={`ไม่มีข้อมูลการรักษาในวันที่ ${dayText}`} />
          ) : (
            <div className={`overflow-x-auto ${dim}`}>
              <table className={`w-full text-[13px] table-fixed ${AGG_MINW}`}>
                <AggColgroup />
                <thead><AggHeadRow label="สาขา" /></thead>
                <tbody>
                  {branches.map((b) => {
                    const on = b.bid === activeBid
                    return (
                      <tr key={b.bid} onClick={() => setSelBranch(b.bid)}
                        tabIndex={0} aria-selected={on}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelBranch(b.bid) } }}
                        className={`cursor-pointer border-b border-slate-50 transition-colors focus-visible:outline-none focus-visible:bg-brand-50/50 ${on ? 'bg-brand-50' : 'hover:bg-slate-50'}`}>
                        <td className={`py-2.5 pl-2 border-l-[3px] ${on ? 'border-brand-500' : 'border-transparent'}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`font-medium truncate ${on ? 'text-brand-700' : 'text-slate-700'}`}>{b.name}</span>
                            {on && <Badge tone="brand" className="shrink-0">กำลังดู</Badge>}
                            {b.noClaimRef > 0 && <Badge tone="rose" className="shrink-0" title={`เบิกแต่ยังไม่มีรหัสเคลม ${b.noClaimRef} รายการ`}><AlertTriangle size={11} />รหัสเคลม {b.noClaimRef}</Badge>}
                            {b.noPayTotal !== 0 && <AlertTriangle size={13} className="shrink-0 text-rose-500" title={`มีเคสที่เก็บเงินจากคนไข้แต่ไม่ระบุวิธีชำระ ${fmt(b.noPayTotal)} (${b.noPayCount} เคส)`} />}
                          </div>
                        </td>
                        <td className="text-right px-2 tabular-nums text-slate-500">{b.tx}</td>
                        <td className="text-right px-2 tabular-nums text-slate-500">{b.patientCount}</td>
                        <AggMoneyCells patient={b.patient} advance={b.advance} total={b.total} clinic={b.clinic} doctor={b.doctor} />
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="text-[13px] font-semibold text-slate-900 border-t-2 border-slate-100">
                    <td className="py-2.5 pl-2 border-l-[3px] border-transparent">รวม</td>
                    <td className="text-right px-2 tabular-nums">{grand.tx}</td>
                    <td className="text-right px-2 tabular-nums">{grand.patients}</td>
                    <AggMoneyCells patient={grand.patient} advance={grand.advance} total={grand.total} clinic={grand.clinic} doctor={grand.doctor} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        {/* sticky drill-down context — stays legible while scrolling the detail + ledger.
            Bleeds to the page edge: the negative margins mirror <Page>'s padding. */}
        {activeBranch && rows?.length ? (
          <div className="sticky top-0 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10 py-2.5 bg-white/85 backdrop-blur-sm border-y border-slate-100 flex items-center gap-2.5">
            <Building2 size={15} className="text-brand-600 shrink-0" strokeWidth={2} />
            <span className="text-[12px] text-slate-400 shrink-0">กำลังดูสาขา</span>
            <span className="text-[13px] font-semibold text-brand-700 truncate">{activeBranch.name}</span>
            <span className="hidden sm:inline text-[11.5px] text-slate-400 tabular-nums shrink-0">· {activeBranch.tx} เคส · รวม {fmt(activeBranch.total)}</span>
            <span className="ml-auto text-[11.5px] text-slate-400 tabular-nums shrink-0">{dayText}</span>
          </div>
        ) : null}

        {/* ── selected-branch detail: summary + payment + doctor ─────────── */}
        {activeBranch && rows?.length ? (
          <Card icon={<Building2 size={18} strokeWidth={1.75} />} title={`รายละเอียดสาขา · ${activeBranch.name}`}
            subtitle={`${activeBranch.tx} เคส · ${activeBranch.patientCount} คนไข้`}>
            <div className={dim}>
              <div className="flex flex-wrap items-center gap-x-7 gap-y-3 mb-5 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                <DetailStat label="รวม" value={activeBranch.patient} color={TEAL} strong />
                <DetailStat label="เบิก" value={activeBranch.advance} color={INDIGO} />
                <DetailStat label="รวม (ยอดจริง)" value={activeBranch.total} strong />
                <DetailStat label="ส่วนคลินิก" value={activeBranch.clinic} color={BLUE} />
                <DetailStat label="ส่วนหมอ" value={activeBranch.doctor} color={PURPLE} />
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet size={15} className="text-slate-400" strokeWidth={1.9} />
                    <h3 className="text-[14px] font-semibold text-slate-800">วิธีชำระเงิน</h3>
                    <span className="text-[12px] text-slate-400">เน้น “รวม” (ยอดที่เก็บจากคนไข้) เพื่อกระทบยอดตอนปิดร้าน</span>
                  </div>
                  <PaymentBreakdown payments={payments} />
                </div>
                <DoctorBreakdown doctors={doctors} branch={activeBranch} />
              </div>
            </div>
          </Card>
        ) : null}

        {/* ── per-patient ledger ─────────────────────────────────────────── */}
        {activeBranch && rows?.length ? (
          <Card icon={<Users size={18} strokeWidth={1.75} />} title={`รายการรักษาแยกตามคนไข้ · ${activeBranch.name}`}
            subtitle={`${filteredPatients.length}${filterActive ? `/${patients.length}` : ''} คนไข้ · ${branchTxs.length} เคส · คลิกเพื่อดูบิล`}
            right={
              <Button variant="secondary" size="sm" icon={allOpen ? Minimize2 : Maximize2} onClick={allOpen ? collapseAll : expandAll}>
                {allOpen ? 'ย่อทั้งหมด' : 'ขยายทั้งหมด'}
              </Button>
            }>
            {/* filters */}
            <FilterCard compact onClear={clearFilters} canClear={filterActive} className="!mb-3">
              <SearchInput value={fPatient} onChange={setFPatient} placeholder="ชื่อ / HN" className="w-52" />
              <Select className="w-[200px]" value={fDoc} onChange={setFDoc}
                options={[{ value: 'all', label: 'ทุกหมอ' }, ...docOptions.map((n) => ({ value: n, label: n }))]} />
              <Select className="w-[180px]" value={fPay} onChange={setFPay}
                options={[{ value: 'all', label: 'ทุกวิธีชำระ' }, ...payOptions.map((o) => ({ value: o.key, label: o.name }))]} />
              <div className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 self-center">
                <span>ยอดรวม</span>
                <Input type="number" value={fMin} onChange={(e) => setFMin(e.target.value)} placeholder="ต่ำสุด" aria-label="ยอดรวมต่ำสุด" className="!w-24" />
                <span className="text-slate-400">–</span>
                <Input type="number" value={fMax} onChange={(e) => setFMax(e.target.value)} placeholder="สูงสุด" aria-label="ยอดรวมสูงสุด" className="!w-24" />
              </div>
            </FilterCard>
            <div className={`rounded-xl border border-slate-200 overflow-hidden ${dim}`}>
              {filteredPatients.length === 0 ? (
                filterActive
                  ? <NoMatchState onClear={clearFilters} />
                  : <EmptyState variant="plain" title="ไม่มีรายการรักษาในสาขานี้" />
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[680px] divide-y divide-slate-100">
                    {filteredPatients.map((p) => (
                      <PatientBlock key={p.pid} p={p} pmById={pmById} billingSort={billingSort} setBillingSort={setBillingSort}
                        open={openPatients.has(p.pid)} onToggle={() => togglePatient(p.pid)} onEditTx={setEditTx} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        ) : null}
      </div>

      {editTx && (
        <EditTreatmentDrawer tx={editTx} pmById={pmById} methodNames={payMethodNames}
          onClose={() => setEditTx(null)}
          onSaved={() => { setEditTx(null); toast.success('บันทึกแล้ว'); setReloadKey((k) => k + 1) }}
          onPartialSave={() => setReloadKey((k) => k + 1)} />
      )}
    </Page>
  )
}

/* -------------------------------------------------------------------------- */
/* income mindmap — shows how รวมทั้งหมด decomposes                            */
/* -------------------------------------------------------------------------- */

function MindLeaf({ label, value, color, strong, title }) {
  return (
    <div className="min-w-[88px]" title={title}>
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />{label}
        {title && <span className="text-slate-300 text-[10px] cursor-help">ⓘ</span>}
      </div>
      <div className="text-[17px] font-bold tabular-nums leading-tight" style={strong ? { color } : { color: '#0f172a' }}>{value}</div>
    </div>
  )
}

function MindGroup({ label, hint, hintColor, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm">
      <div className="text-[11px] font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
        {label}{hint && <span className="text-[10px] px-1 py-0.5 rounded" style={{ color: hintColor, background: hintColor + '18' }}>{hint}</span>}
      </div>
      <div className="flex items-center gap-5">{children}</div>
    </div>
  )
}

function IncomeMindmap({ grand, branchCount, advanceByPayer = [] }) {
  // เบิก = all non-patient money = รวม − เก็บเอง (includes both tagged priceadvance
  // AND direct-claim "เบิกคืนบริษัท" reversals). So เก็บเอง + เบิก = รวม exactly.
  const advanceTotal = grand.total - grand.patient
  const [showAllPayers, setShowAllPayers] = useState(false)
  const PAYER_LIMIT = 5
  const shownPayers = showAllPayers ? advanceByPayer : advanceByPayer.slice(0, PAYER_LIMIT)
  const hiddenPayers = advanceByPayer.length - PAYER_LIMIT
  return (
    <div className="mb-5 flex items-start gap-3 overflow-x-auto pb-1">
      {/* root */}
      <div className="shrink-0 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 text-white px-4 py-3 shadow-sm">
        <div className="text-[11px] font-medium text-slate-300">รวมทั้งหมด · ยอดจริง</div>
        <div className="text-[24px] font-bold tabular-nums leading-tight">{fmt(grand.total)}</div>
        <div className="text-[10.5px] text-slate-400 mt-0.5">{grand.tx} เคส · {grand.patients} คนไข้ · {branchCount} สาขา</div>
      </div>
      <div className="shrink-0 flex flex-col gap-2.5">
        <MindGroup label="ที่มาของเงิน" hint="= รวมทั้งหมด" hintColor="#64748b">
          <MindLeaf label="เก็บจากคนไข้" value={fmt(grand.patient)} color={TEAL} strong />
          <MindLeaf label="เบิก" value={fmt(advanceTotal)} color={INDIGO} strong />
        </MindGroup>
        <MindGroup label="การแบ่งรายได้" hint="= รวมทั้งหมด" hintColor="#64748b">
          <MindLeaf label="ส่วนคลินิก" value={fmt(grand.clinic)} color={BLUE} />
          <MindLeaf label="ส่วนหมอ" value={fmt(grand.doctor)} color={PURPLE} />
        </MindGroup>
      </div>
      {advanceByPayer.length > 0 && (
        <div className="shrink-0">
        <MindGroup label="รายละเอียดการเบิก" hint={`รวม ${fmt(advanceTotal)}`} hintColor={INDIGO}>
          <div className="min-w-[128px] space-y-0.5">
            {shownPayers.map(([payer, amt]) => (
              <div key={payer} className="flex items-center justify-between gap-3 text-[11px]">
                <span className="text-slate-500 truncate">{payer}</span>
                <span className="tabular-nums font-medium text-slate-700">{fmt(amt)}</span>
              </div>
            ))}
            {hiddenPayers > 0 && (
              <button
                type="button"
                onClick={() => setShowAllPayers((v) => !v)}
                className="text-[10.5px] font-medium text-indigo-500 hover:text-indigo-700 hover:underline cursor-pointer"
              >
                {showAllPayers ? 'ย่อ' : `+${hiddenPayers} สิทธิ`}
              </button>
            )}
          </div>
        </MindGroup>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* doctor breakdown (inside expanded branch row)                              */
/* -------------------------------------------------------------------------- */

function DoctorBreakdown({ doctors, branch }) {
  const { earners, zero } = doctors
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Stethoscope size={15} className="text-slate-400" strokeWidth={1.9} />
        <h3 className="text-[14px] font-semibold text-slate-800">รายได้แยกตามหมอ</h3>
        <span className="text-[12px] text-slate-400">{earners.length} หมอมีรายได้</span>
      </div>
      {earners.length === 0 ? (
        <div className="text-[13px] text-slate-400 py-2">ไม่มีหมอที่มีรายได้ในวันนี้</div>
      ) : (
        <div className="overflow-x-auto">
          <table className={`w-full text-[13px] table-fixed ${AGG_MINW}`}>
            <AggColgroup />
            <thead><AggHeadRow label="หมอ" /></thead>
            <tbody>
              {earners.map((g) => (
                <tr key={g.id} className="border-b border-slate-50">
                  <td className="py-2.5 pl-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <DoctorAvatar src={g.img} name={g.name} />
                      <span className="font-medium text-slate-700 truncate">{g.name}</span>
                    </div>
                  </td>
                  <td className="text-right px-2 tabular-nums text-slate-500">{g.cases}</td>
                  <td className="text-right px-2 tabular-nums text-slate-500">{g.patientCount}</td>
                  <AggMoneyCells patient={g.patient} advance={g.advance} total={g.total} clinic={g.clinic} doctor={g.doctor} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {zero.length > 0 && (
        <p className="mt-2 text-[11.5px] text-slate-400">
          <span className="font-medium text-slate-500">หมอที่ไม่มีรายได้วันนี้ ({zero.length}):</span> {zero.join(', ')}
        </p>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* charts                                                                      */
/* -------------------------------------------------------------------------- */

function ChartPane({ title, loading, refreshing = false, err, axes, sum, color, compare = false, prevLabel, prevSum }) {
  const [kind, setKind] = usePersistedState('tx:chartKind', 'bar') // 'bar' | 'radar' — shared A/B toggle
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-3">
      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-[13px] font-semibold text-slate-700 truncate">{title}</h3>
          {compare && prevLabel && (
            <span className="hidden md:inline-flex items-center gap-1 text-[11px] text-slate-400 shrink-0">
              <span className="inline-block w-3 h-1.5 rounded-sm bg-slate-300" />{prevLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {compare && prevSum != null && <span className="text-[12px] font-medium tabular-nums text-slate-400">{fmt(prevSum)}</span>}
          <span style={{ color }} className="text-[13px] font-semibold tabular-nums">{fmt(sum)}</span>
          <Segmented size="sm" value={kind} onChange={setKind}
            options={[{ value: 'bar', label: '', icon: BarChart3, title: 'กราฟแท่ง' }, { value: 'radar', label: '', icon: Radar, title: 'กราฟเรดาร์' }]} />
        </div>
      </div>
      {/* first load → spinner; a refresh keeps the chart and dims it (no blink) */}
      {loading ? <div className="h-[320px] flex items-center justify-center"><Loading /></div>
        : err ? <div className="h-[320px] flex items-center"><ErrorState className="w-full" message={err} /></div>
        : (
          <div className={`transition-opacity ${refreshing ? 'opacity-60' : ''}`}>
            {kind === 'radar'
              ? <RadarChart axes={axes} color={color} compare={compare} height={320} compact emptyText="ไม่มีข้อมูล" />
              : <BarCompareChart axes={axes} color={color} compare={compare} height={320} compact emptyText="ไม่มีข้อมูล" />}
          </div>
        )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* shared aggregate-table scaffold — branch / payment / doctor use the SAME    */
/* columns + widths so they align vertically. Order: เก็บเอง·เบิก·รวม·คลินิก·หมอ  */
/* -------------------------------------------------------------------------- */

// label flexible (null) + 7 fixed numeric columns
const AGG_WIDTHS = [null, 52, 52, 98, 84, 98, 84, 84]
const AGG_MINW = 'min-w-[720px]'

function AggColgroup() {
  return <colgroup>{AGG_WIDTHS.map((w, i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
}

// billing-line table: รายการ(flex) · จำนวน · เก็บเอง · เบิก · รวม · คลินิก · หมอ

function AggHeadRow({ label }) {
  const th = 'text-right font-medium py-2 px-2'
  return (
    <tr className="text-[11.5px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
      <th className="text-left font-medium py-2 pl-2">{label}</th>
      <th className={th}>เคส</th>
      <th className={th}>คนไข้</th>
      <th className="text-right font-semibold py-2 px-2 bg-teal-50/70" style={{ color: TEAL }}>รวม</th>
      <th className={th}>เบิก</th>
      <th className={th}>รวมเบิก</th>
      <th className={th}>คลินิก</th>
      <th className="text-right font-medium py-2 pr-2">หมอ</th>
    </tr>
  )
}

// the 5 money cells (เก็บเอง·เบิก·รวม·คลินิก·หมอ) shared by every aggregate row
function AggMoneyCells({ patient, advance, total, clinic, doctor, alert }) {
  return (
    <>
      <td className="text-right px-2 tabular-nums font-bold text-[13.5px] bg-teal-50/60 whitespace-nowrap" style={{ color: TEAL }}>{fmt(patient)}</td>
      <td className="text-right px-2 tabular-nums text-indigo-500 whitespace-nowrap">{advance ? fmt(advance) : <span className="text-slate-300">—</span>}</td>
      <td className={`text-right px-2 tabular-nums font-semibold whitespace-nowrap ${alert ? 'text-rose-600' : 'text-slate-900'}`}>{fmt(total)}</td>
      <td className="text-right px-2 tabular-nums text-slate-500 whitespace-nowrap">{fmt(clinic)}</td>
      <td className="text-right pr-2 tabular-nums text-slate-500 whitespace-nowrap">{fmt(doctor)}</td>
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* payment breakdown (inside branch card)                                     */
/* -------------------------------------------------------------------------- */

function PaymentBreakdown({ payments }) {
  const { rows } = payments
  const payMax = Math.max(1, ...rows.map((r) => r.patient || 0))
  const noPay = rows.find((r) => isNoPay(r.key))
  return (
    <>
      {noPay && Math.abs(noPay.patient) > 0.5 && (
        <Banner tone="error" className="mb-3">
          <span className="font-semibold">ต้องตรวจสอบ:</span> มี {noPay.count} เคส (รวม {fmt(noPay.patient)} บาท) ที่ <span className="font-semibold">เก็บเงินจากคนไข้แต่ยังไม่ระบุวิธีชำระ</span>
        </Banner>
      )}
      <div className="overflow-x-auto">
        <table className={`w-full text-[13px] table-fixed ${AGG_MINW}`}>
          <AggColgroup />
          <thead><AggHeadRow label="วิธีชำระ" /></thead>
          <tbody>
            {rows.map((r) => {
              const alert = isNoPay(r.key) && Math.abs(r.patient) > 0.5
              return (
                <tr key={r.key} className={`border-b border-slate-50 ${alert ? 'bg-rose-50/50' : ''}`}>
                  <td className="py-2 pl-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color }} />
                      <span className={`font-medium truncate ${alert ? 'text-rose-600' : 'text-slate-700'}`}>{r.name}</span>
                      {alert && <AlertTriangle size={13} className="shrink-0 text-rose-500" />}
                    </div>
                    <div className="flex items-center gap-2 mt-1 pr-3">
                      <div className="flex-1 h-1.5 rounded bg-slate-100 min-w-[40px]">
                        <div className="h-full rounded" style={{ width: `${(Math.max(0, r.patient) / payMax) * 100}%`, background: TEAL, minWidth: r.patient > 0 ? 2 : 0 }} />
                      </div>
                      <span className="w-8 text-right tabular-nums text-[11px] text-slate-400">{r.pct.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="text-right px-2 tabular-nums text-slate-500 align-top pt-2.5">{r.count}</td>
                  <td className="text-right px-2 tabular-nums text-slate-500 align-top pt-2.5">{r.patientCount}</td>
                  <td className="text-right px-2 tabular-nums font-bold text-[13.5px] bg-teal-50/60 align-top pt-2.5 whitespace-nowrap" style={{ color: TEAL }}>{fmt(r.patient)}</td>
                  <td className="text-right px-2 tabular-nums text-indigo-500 align-top pt-2.5 whitespace-nowrap">{r.advance ? fmt(r.advance) : <span className="text-slate-300">—</span>}</td>
                  <td className={`text-right px-2 tabular-nums align-top pt-2.5 whitespace-nowrap ${alert ? 'text-rose-600 font-semibold' : 'text-slate-900 font-semibold'}`}>{fmt(r.total)}</td>
                  <td className="text-right px-2 tabular-nums text-slate-500 align-top pt-2.5 whitespace-nowrap">{fmt(r.clinic)}</td>
                  <td className="text-right pr-2 tabular-nums text-slate-500 align-top pt-2.5 whitespace-nowrap">{fmt(r.doctor)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* patient block → billing (single treatment) or groups by doctor+payment      */
/* -------------------------------------------------------------------------- */

function PatientBlock({ p, pmById, open, onToggle, billingSort, setBillingSort, onEditTx }) {
  const single = p.txs.length === 1
  const tx0 = p.txs[0]
  const flags = p.txs.reduce((s, tx) => s + (tx.billing || []).filter(bMismatch).length, 0)
  const noPay = p.txs.some((tx) => isNoPay(paymentOf(tx, pmById).key) && Math.abs(sumTx(tx).patient) > 0.5)
  const noClaimRef = p.txs.reduce((s, tx) => s + (tx.billing || []).filter(bMissingClaimRef).length, 0)

  // single-treatment meta pulled up into the patient header
  const pay0 = single ? paymentOf(tx0, pmById) : null
  const apptType = single ? (tx0.appointmentlist?.type || tx0.type || '') : ''
  const doc0 = single ? doctorName(tx0) : ''
  const note0 = single ? String(tx0.detail || tx0.remark || '').trim() : ''
  // multi: one editable sub-block per treatment (labelled by doctor + payment)
  const txsSorted = single ? [] : [...p.txs].sort((a, b) => sumTx(b).total - sumTx(a).total)

  // avatar + name + HN → the patient page in a new tab (kit PatientLink /
  // PatientName: real <a>, stopPropagation so the row click still only toggles
  // the block). groupByPatient falls back to a `nk-<txid>` key for treatments
  // with no patient record — the kit renders those as plain text.
  const avatar = (
    <span className="w-8 h-8 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[13px] font-semibold shrink-0">{nameInitial(p.name)}</span>
  )

  return (
    <div id={`tx-pt-${p.pid}`}>
      {/* patient header — a BillColgroup row so its totals align with the billing columns */}
      <table className={`w-full text-[13px] table-fixed cursor-pointer ${open ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}>
        <BillColgroup />
        <tbody>
          <tr onClick={onToggle} aria-expanded={open}>
            <td className="py-3 pl-4 pr-2 align-middle">
              <div className="flex items-center gap-3 min-w-0">
                <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
                <PatientLink patientId={p.pid} className="shrink-0 rounded-full hover:ring-2 hover:ring-brand-200">{avatar}</PatientLink>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <PatientName inline patient={{ id: p.pid, name: p.name, HN: p.HN }} />
                    {p.pt_status && <Badge className="shrink-0">{p.pt_status}</Badge>}
                    {single && apptType && <Badge>{apptType}</Badge>}
                    {single && <PaymentBadge name={pay0.name} color={pay0.color} none={isNoPay(pay0.key)} />}
                    {noPay && !single && <Badge tone="rose"><AlertTriangle size={11} />ไม่ระบุวิธีชำระ</Badge>}
                    {flags > 0 && <Badge tone="amber"><AlertTriangle size={11} />ตรวจสอบ {flags}</Badge>}
                    {noClaimRef > 0 && <Badge tone="rose"><AlertTriangle size={11} />ไม่มีรหัสเคลม{noClaimRef > 1 ? ` ${noClaimRef}` : ''}</Badge>}
                    {single && <EditTxButton onClick={(e) => { e.stopPropagation(); onEditTx(tx0) }} />}
                  </div>
                  <div className="text-[11.5px] text-slate-400 mt-0.5 truncate">
                    {single
                      ? <>{dayWithDow(tx0.date)}{doc0 ? ` · ${doc0}` : ''} · {(tx0.billing || []).length} รายการ{note0 && <span className="text-slate-500"> — {note0}</span>}</>
                      : `${p.txs.length} เคส`}
                  </div>
                </div>
              </div>
            </td>
            <BillTotalCells patient={p.patient} advance={p.advance} total={p.total} clinic={p.clinic} doctor={p.doctor} />
          </tr>
        </tbody>
      </table>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/30">
          {single
            ? <BillingTable lines={tx0.billing || []} showFooter={false} sort={billingSort} onSort={setBillingSort} />
            : txsSorted.map((tx, i) => {
                const s = sumTx(tx), pay = paymentOf(tx, pmById), doc = doctorName(tx)
                const tnote = String(tx.detail || tx.remark || '').trim()
                return (
                  <div key={tx.id ?? i} className="border-b border-slate-100 last:border-b-0">
                    {/* per-treatment sub-header — BillColgroup row → aligns with its billing table */}
                    <table className="w-full text-[12.5px] table-fixed bg-slate-100/60">
                      <BillColgroup />
                      <tbody>
                        <tr>
                          <td className="py-2 pl-4 pr-2">
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <span className="text-[12.5px] font-semibold text-slate-700 truncate">{doc || '—'}</span>
                              <PaymentBadge name={pay.name} color={pay.color} none={isNoPay(pay.key)} />
                              {tx.date && <span className="text-[11px] text-slate-400 tabular-nums">{fmtDate(tx.date)}</span>}
                              {tnote && <span className="text-[11px] text-slate-400 truncate">— {tnote}</span>}
                              <EditTxButton onClick={() => onEditTx(tx)} />
                            </div>
                          </td>
                          <BillTotalCells patient={s.patient} advance={s.advance} total={s.total} clinic={s.clinic} doctor={s.doctor} />
                        </tr>
                      </tbody>
                    </table>
                    <BillingTable lines={tx.billing || []} showFooter={false} sort={billingSort} onSort={setBillingSort} />
                  </div>
                )
              })}
        </div>
      )}
    </div>
  )
}

function EditTxButton({ onClick }) {
  return <IconButton icon={Pencil} label="แก้ไขข้อมูลการรักษา" size="sm" onClick={onClick} className="shrink-0" />
}

/* -------------------------------------------------------------------------- */
/* edit-treatment drawer — edits payment method + detail/remark of one treatment */
/* (a record edit → kit Drawer: Esc, guarded close while saving, ทิ้งการแก้ไข?) */
/* -------------------------------------------------------------------------- */

function EditTreatmentDrawer({ tx, pmById, methodNames = [], onClose, onSaved, onPartialSave }) {
  const tid = tx.id ?? tx.treatment_id
  const toast = useToast()
  const [initial] = useState(() => {
    const po = paymentOf(tx, pmById)
    return { payment: isNoPay(po.key) ? '' : po.name, detail: String(tx.detail ?? ''), remark: String(tx.remark ?? '') }
  })
  const [payment, setPayment] = useState(initial.payment)
  const [detail, setDetail] = useState(initial.detail)
  const [remark, setRemark] = useState(initial.remark)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const dirty = payment !== initial.payment || detail !== initial.detail || remark !== initial.remark

  // options = canonical Xano payment_method list (id order), + this tx's current value as a fallback
  const options = useMemo(() => {
    const list = []
    const seen = new Set()
    const add = (v) => { const n = String(v ?? '').trim(); if (n && !seen.has(n)) { seen.add(n); list.push(n) } }
    for (const name of pmById.values()) add(name)
    const po = paymentOf(tx, pmById)
    if (!isNoPay(po.key)) add(po.name)
    add(payment)
    // Most-used first, not Xano's insertion order — see sortPayMethods.
    return sortPayMethods(list)
  }, [pmById, tx, payment])

  const s = sumTx(tx)
  // วิธีชำระเงินแก้ได้เฉพาะ "วันที่ทำการรักษา" ตามเวลาไทย — กันการแก้ย้อนหลัง
  const txDateISO = String(tx.date ?? '').slice(0, 10)
  const bkkToday = useMemo(() => bkkTodayISO(), [])
  const paymentLocked = !txDateISO || txDateISO !== bkkToday
  const noPayRisk = !paymentLocked && !payment && Math.abs(s.patient) > 0.5

  async function save() {
    setSaving(true); setErr('')
    try {
      const body = { detail, remark }
      if (!paymentLocked) body.paymentMethod = payment // never rewrite a past day's method
      const r = await patchJson(`/api/treatments/${tid}`, body)
      // The proxy applies the fields it accepts and names the ones it refused
      // (its same-day check is computed per request; this drawer's is computed
      // once at mount, so they can disagree across Bangkok midnight). Refusing
      // quietly would look like a successful save on a field that splits
      // df_trace from df_nontrace in payroll.
      if (r?.refused_fields?.length) {
        // detail/remark DID land upstream — refresh the ledger so the row stops
        // showing pre-edit text, but keep the drawer open on the error rather
        // than dismissing it. fetchDayCached caches past days for an hour, so a
        // skipped refresh would look stale for the rest of the session.
        onPartialSave?.()
        const msg = r.refused_detail || 'บางรายการแก้ไขไม่ได้ — กรุณารีเฟรชแล้วลองใหม่'
        setErr(msg)
        toast.error('บันทึกได้ไม่ครบ', { detail: msg })
        setSaving(false)
        return
      }
      onSaved()
    } catch (e) {
      const msg = e.message || 'บันทึกไม่สำเร็จ'
      setErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
      setSaving(false)
    }
  }

  return (
    <Drawer open onClose={onClose} title="แก้ไขข้อมูลการรักษา" icon={Pencil} width="sm"
      busy={saving} dirty={dirty} record={tx} recordId={tid} recordTable="treatment"
      footer={<DrawerFooter onCancel={onClose} onSave={save} saving={saving} />}>
      <div className="space-y-4">
        <div className="rounded-xl bg-white border border-slate-200 px-3.5 py-3 text-[13px]">
          <PatientName inline patient={{ id: tx.patient_id ?? tx.patient?.id, name: stripStars(tx.fullname) || '—', HN: tx.HN }} />
          <div className="text-[12px] text-slate-500 mt-0.5">
            {tx.date ? fmtDate(tx.date) : ''}{doctorName(tx) ? ` · ${doctorName(tx)}` : ''} · รวม {fmt(s.patient)} · รวมเบิก {fmt(s.total)} บาท
          </div>
        </div>

        <Field
          label={<span className="inline-flex items-center gap-1.5">วิธีชำระเงิน{paymentLocked && <Lock size={12} className="text-slate-400" strokeWidth={2} />}</span>}
          hint={paymentLocked ? 'เปลี่ยนวิธีชำระเงินได้เฉพาะวันที่ทำการรักษา (เวลาไทย)' : undefined}
          error={!paymentLocked && noPayRisk ? 'มีการเก็บเงินจากคนไข้ ควรระบุวิธีชำระเงิน' : undefined}>
          <Select value={payment} onChange={setPayment} disabled={paymentLocked}
            options={[{ value: '', label: '— ไม่ระบุ —' }, ...options.map((n) => ({ value: n, label: n }))]} />
        </Field>

        <Field label="รายละเอียด">
          <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="รายละเอียดการรักษา" />
        </Field>

        <Field label="หมายเหตุ">
          <Input value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="หมายเหตุ" />
        </Field>

        <InlineError>{err}</InlineError>
      </div>
    </Drawer>
  )
}

function DoctorAvatar({ src, name }) {
  const [err, setErr] = useState(false)
  if (src && !err) {
    return <img src={src} alt="" onError={() => setErr(true)} className="w-6 h-6 rounded-full object-cover shrink-0 bg-slate-100 border border-slate-200" />
  }
  return <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{nameInitial(name)}</span>
}

function DetailStat({ label, value, color, strong }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-500">
        {color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}{label}
      </div>
      <div className={`tabular-nums leading-tight ${strong ? 'text-[19px] font-bold' : 'text-[16px] font-semibold text-slate-700'}`}
        style={strong ? { color: color || '#0f172a' } : undefined}>{fmt(value)}</div>
    </div>
  )
}

